import cn from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI } from 'sonata-react-components';

import { NewFeaturesStyle } from './newsFeatures.style';

import type { News } from 'sonata-static-storage/lib/schemas/news/news.schema';

type Content = News['pages'][number] & {
  id: string;
};

type State = {
  shownContentIndex: number;
};

type Props = {
  areButtonsDisabled?: boolean;
  content: Content[];
  link?: string;
  onChange?: (index: number) => void;
  onClose?: () => void;
};

export function NewsFeatures({ areButtonsDisabled, content, link, onChange, onClose }: Props) {
  const { formatMessage } = useIntl();
  const [{ shownContentIndex }, setState] = React.useState<State>({
    shownContentIndex: 0
  });

  const currentContent = content[shownContentIndex];
  const isFirstItem = shownContentIndex === 0;
  const isLastItem = shownContentIndex >= content.length - 1;
  const descriptionHTML = { __html: currentContent?.description || '' };

  const updateShownContentIndex = (index: number): void => {
    setState(prevState => ({
      ...prevState,
      shownContentIndex: index
    }));
  };

  const handleClickOnCarrouselItem = (index: number): void => {
    updateShownContentIndex(index);
    onChange?.(currentContent.page);
  };

  const handleClickNext = (): void => {
    const index = shownContentIndex + 1;
    updateShownContentIndex(index);
    onChange?.(currentContent.page);
  };

  const handleClickBack = (): void => {
    const index = shownContentIndex - 1;
    if (index >= 0) {
      updateShownContentIndex(index);
      onChange?.(currentContent.page);
    }
  };

  const handleClickLink = (): void => {
    if (link) {
      window.open(link, '_blank');
    }
    onChange?.(currentContent.page);
    onClose?.();
  };

  if (!currentContent) {
    return null;
  }

  return (
    <NewFeaturesStyle className="newFeatures" data-testid="newFeatures">
      <div className="newFeatures__image">
        <img alt={currentContent.title} src={currentContent.image} />
      </div>
      <div className="newFeatures__info">
        <h4 className="newFeatures__title" data-testid="featureTitle">
          {currentContent.title}
        </h4>
        <p className="newFeatures__description" dangerouslySetInnerHTML={descriptionHTML} />
      </div>
      <div className="newFeatures__carrousel">
        {content.map(({ id }, index) => (
          <div
            className={cn('newFeatures__carrouselItem', {
              'newFeatures__carrouselItem--active': index === shownContentIndex
            })}
            data-testid={`testId_Carrousel${id}`}
            key={id}
            onClick={() => handleClickOnCarrouselItem(index)}
          />
        ))}
      </div>
      <div className="newFeatures__buttonSection">
        {!isFirstItem ? (
          <ButtonUI
            disabled={areButtonsDisabled}
            icon="far fa-arrow-left"
            iconPosition="left"
            id="backButton"
            onClick={handleClickBack}
            text={formatMessage({
              defaultMessage: 'Back',
              id: 'common.back'
            })}
            type="secondary"
          />
        ) : null}
        {isLastItem ? (
          <ButtonUI
            disabled={areButtonsDisabled}
            iconPosition="right"
            id="linkButton"
            onClick={handleClickLink}
            text={formatMessage({
              defaultMessage: 'Get started!',
              id: 'menu.newFeatures.buttons.link'
            })}
          />
        ) : (
          <ButtonUI
            disabled={isLastItem || areButtonsDisabled}
            icon="far fa-arrow-right"
            iconPosition="right"
            id="nextButton"
            onClick={handleClickNext}
            text={formatMessage({
              defaultMessage: 'Next',
              id: 'common.next'
            })}
          />
        )}
      </div>
    </NewFeaturesStyle>
  );
}
